<template>
  <div :class="$style.buttonsContainer">
    <FlatActionButton
      v-if="isOAuth2"
      :action="handleAuthorizeClick"
      :buttonClass="[
        'btnCTA',
        'btnDefault',
        {
          'btn-primary': enableAuthorize
        },
      ]"
      data-test-id="GenericScimConnectorNewConfigButtons_Authorize"
      :disabled="!enableAuthorize"
      :loading="testButtonLoading"
      text="Authorize"
    />
    <!-- Test Button -->
    <FlatActionButton
      v-else
      :action="handleTestConnectionClick"
      :buttonClass="[
        'btnCTA',
        'btnDefault',
        {
          'btn-primary': !disableTest
        },
      ]"
      data-test-id="GenericScimConnectorNewConfigButtons_testConnection"
      :disabled="disableTest"
      :loading="testButtonLoading"
      text="Test Connection"
    />
    <!-- Activate Button  -->
    <FlatActionButton
      :action="handleActivateButtonClick"
      :buttonClass="[
        'btnCTA',
        'btnDefault',
        {
          'btn-primary': !disableActivate
        },
      ]"
      data-test-id="GenericScimConnectorNewConfigButtons_activate"
      :disabled="disableActivate"
      :loading="activateButtonLoading"
      text="Activate"
    />
    <OauthCredentialsWarningModal
      :showOauthModal="showOauthModal"
      @close="handleModalClose"
      @continue="handleOauthModalContinue"
    />
  </div>
</template>

<script>
import { CustomOAuthMessages } from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import OauthCredentialsWarningModal from 'client/applications/scim/customConnector/OauthCredentialsWarningModal.vue';

const { success, error } = Constants;
const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];

export default {
  name: 'GenericScimConnectorNewConfigButtons',

  components: {
    FlatActionButton,
    OauthCredentialsWarningModal,
  },

  mixins: [IdentityManagementMixin],

  props: {
    enableTestConnectionButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activateButtonLoading: false,
      testButtonLoading: false,
      showOauthModal: false,
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      tokenKey: 'scimTokenKey',
    }),

    disableActivate() {
      let disable;

      if (this.scimConnectionType === 'SCIM') {
        disable = !this.isTestConnectionPassed;
      } else if (this.scimConnectionType === 'CUSTOM') {
        // Active BTN should be disabled WHEN:
        // Missing required fields for the test connection request OR
        // Test Connection has yet to pass OR
        // Missing required attributes for activate request
        const { companyEmail, username } = this.customIdmMappingAttributes;
        const areRequiredAttributesValid = companyEmail !== '' && username !== '';
        disable = !this.enableTestConnectionButton
          || !this.isTestConnectionPassed
          || !areRequiredAttributesValid;
      }

      return disable;
    },

    disableTest() {
      if (this.isTestConnectionPassed) {
        return true;
      }

      return !this.enableTestConnectionButton;
    },

    enableAuthorize() {
      return this.didOauthConfigChanged && this.enableTestConnectionButton;
    },
  },

  methods: {
    ...mapActions(ApplicationModel, [
      'verifyScimConnection',
      'activateIdmWithOauth',
      'reactivateOauth',
      'updateCustomIdmOauthConfiguration',
      'setIdmOauthConfigured',
    ]),

    async handleActivateButtonClick() {
      if (this.isNew) {
        // For new applications:
        // Hitting "save" when the modal appears will activate SCIM and will skip the
        // interaction with the "activate" button. This is a UX problem to address
        // Saved applications however, do go through the intended flow indicated by CS-1709
        this.emitScimEvent('showScimModal', { scimConnectorType: 'generic' });
      } else {
        try {
          this.activateButtonLoading = true;
          if (this.isOAuth2) {
            const { data } = await this.activateIdmWithOauth();
            const { disabled: isIdmClientDisabled } = data;
            if (!isIdmClientDisabled) {
              this.setScimStatus(true);
              this.setOldCustomApiConfig();
            }
          } else {
            await this.configureScim();
            await this.activateScim();
          }

          this.setGenericOldData();
          notify(success, 'Identity Management integration has been successfully verified.');
          this.activateButtonLoading = false;
        } catch {
          notify(error, 'There was a problem activating Identity Management.');
          this.activateButtonLoading = false;
        }
      }
    },

    async handleAuthorizeClick() {
      this.testButtonLoading = true;
      try {
        if (this.isTestConnectionPassed) {
          this.showOauthModal = true;
        } else {
          await this.configureScim();
        }
      } catch (responseError) {
        notify(error, responseError.message);
      }

      if (this.redirectLocation !== '') {
        this.handleRedirectConfigure();
      }
      this.testButtonLoading = false;
    },

    handleModalClose() {
      this.showOauthModal = false;
    },

    async handleOauthModalContinue() {
      try {
        await this.updateCustomIdmOauthConfiguration();
        if (this.redirectLocation !== '') {
          this.handleRedirectConfigure();
        }
      } catch {
        this.triggerErrorNotification(CustomOAuthMessages.get('AUTH_FAILURE'));
      }
    },

    async handleTestConnectionClick() {
      this.testButtonLoading = true;

      try {
        await this.verifyScimConnection();
        this.setScimGroupsEnable(true);
        notify(success, 'Connection successful. Follow the next steps to finalize the integration.');
      } catch {
        notify(error, 'There was a problem testing the connection. Please try again.');
      }

      this.testButtonLoading = false;

      if (this.isScimActive) {
        this.disableTestButton = true;
      }
    },
  },
};
</script>

<style module>
.buttonsContainer button:last-child {
  margin-left: 16px;
}
</style>
